import { UserPlus, Images, Wand2, Share2 } from "lucide-react";

const steps = [
  {
    icon: UserPlus,
    number: "01",
    title: "Start With a Few Details",
    description: "Begin the tribute by sharing basic information about your loved one. Everything else unfolds naturally from there.",
    color: "bg-[hsl(210,30%,95%)]", // Soft blue-gray (midnight blue tint)
  },
  {
    icon: Images,
    number: "02",
    title: "Gather Memories & Moments",
    description: "Upload photos, share stories, and invite others to join in. Collaboration is seamless, supportive, and secure.",
    color: "bg-[hsl(40,40%,94%)]", // Warm cream-gold tint
  },
  {
    icon: Wand2,
    number: "03",
    title: "Build the Tribute Together",
    description: "Create an obituary, eulogy, and memorial video with guided tools that help you express what matters most.",
    color: "bg-[hsl(350,30%,94%)]", // Soft dusty rose tint
  },
  {
    icon: Share2,
    number: "04",
    title: "Publish & Share the Legacy",
    description: "Launch a beautiful tribute page, download all assets, and share the memorial with friends, family, and communities everywhere.",
    color: "bg-[hsl(160,25%,93%)]", // Soft sage green
  },
];

export const HowItWorks = () => {
  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            How It Works
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Creating a meaningful tribute is simple, supportive, and collaborative
          </p>
        </div>
        
        {/* Desktop horizontal flow */}
        <div className="hidden lg:block relative">
          {/* Connection line */}
          <div className="absolute top-24 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-border via-accent/30 to-border" />
          
          <div className="grid grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {/* Number badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-2.5 py-1 rounded-full z-10">
                    {step.number}
                  </div>
                  
                  {/* Card */}
                  <div className={`${step.color} rounded-2xl p-8 h-full flex flex-col items-center text-center transition-transform hover:-translate-y-1 duration-300`}>
                    <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-6 shadow-sm">
                      <Icon className="h-7 w-7 text-accent" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Mobile/Tablet stacked view */}
        <div className="lg:hidden space-y-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className={`${step.color} rounded-2xl p-6 flex gap-5`}
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <Icon className="h-6 w-6 text-accent" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-accent text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                      {step.number}
                    </span>
                    <h3 className="text-lg font-semibold text-foreground">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
